import { service } from '@maia/attachments';
import {
  IconAngleDown,
  IconAngleRight,
  IconDownloadAlt,
  IconFile,
} from '@maia/icons';
import { ActionButton } from '@maia/ui';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { TableCell } from '@maia/ui';
import { downloadEncodedDocument } from '@maia/utils';
import {
  ColumnDef,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { twMerge } from 'tailwind-merge';

const columns: ColumnDef<{
  guid: string;
  name: string;
  reason?: string;
  children?: Array<{ guid: string; name: string; reason?: string }>;
}>[] = [
  {
    accessorKey: 'reference',
    header: 'Documenten',
    cell: ({ row }) => {
      return (
        <div className={twMerge('flex items-center', row.depth > 0 && 'pl-32')}>
          {row.getCanExpand() ? (
            <ActionButton
              className="bg-white mr-8"
              icon={row.getIsExpanded() ? IconAngleDown : IconAngleRight}
              onPress={row.getToggleExpandedHandler()}
            />
          ) : (
            row.depth > 0 && <IconFile className="size-32 p-8" />
          )}
          <div className="inline-flex flex-col">
            <TableCell.Text
              weight={row.depth === 0 ? 'medium' : 'regular'}
              text={row.original.name}
            />
            {row.original.reason && (
              <TableCell.Text
                weight="regular"
                text={row.original.reason}
                className="text-grayscale300 mt-4"
              />
            )}
          </div>
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: 'actions',
    cell: ({ row }) => {
      const { refetch } = useQuery({
        queryFn: () => service.find(row.original.guid),
        queryKey: [row.original.guid],
        enabled: false,
      });

      const handleDownload = useCallback(async () => {
        const documentData = await refetch().then((d) => d.data);
        if (documentData) {
          return downloadEncodedDocument(documentData);
        }
      }, []);
      return (
        <div className="flex gap-x-12 items-center">
          <ActionButton
            icon={IconDownloadAlt}
            size="small"
            onPress={handleDownload}
          />
        </div>
      );
    },
  },
];

export function useAttachmentTable(
  data?: Array<{
    guid: string;
    name: string;
    reason?: string;
    children?: Array<{
      guid: string;
      type: string;
      name: string;
      reason?: string;
    }>;
  }>,
) {
  return useReactTable({
    columns,
    data: data ?? [],
    rowCount: data?.length,
    getSubRows: (row) => row.children,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });
}
